import React, { useState, useRef } from "react";
import { Camera, Upload, X, User } from "lucide-react";
import Button from "./Button";
import { profileService } from "../../lib/profileService";

const ProfilePhotoUpload = ({
  currentPhotoUrl,
  onPhotoUpdated,
  userId,
  size = "large",
  className = "",
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentPhotoUrl);
  const fileInputRef = useRef(null);

  const sizeClasses = {
    small: "w-16 h-16",
    medium: "w-24 h-24",
    large: "w-32 h-32",
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      setIsUploading(true);

      // Client-side validation
      if (!file.type.startsWith("image/")) {
        throw new Error("Please select a valid image file");
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error("Image size must be less than 5MB");
      }

      // Create preview URL
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);

      // Upload the file
      const result = await profileService.uploadProfilePhoto(userId, file);

      if (result.success) {
        setPreviewUrl(result.photoUrl);
        onPhotoUpdated?.(result.photoUrl);
      } else {
        throw new Error(result.error || "Failed to upload photo");
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      // Revert preview on error
      setPreviewUrl(currentPhotoUrl);
      alert(error.message || "Failed to upload photo");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      setIsUploading(true);

      const result = await profileService.removeProfilePhoto(userId);

      if (result.success) {
        setPreviewUrl(null);
        onPhotoUpdated?.(null);
      } else {
        throw new Error(result.error || "Failed to remove photo");
      }
    } catch (error) {
      console.error("Error removing photo:", error);
      alert(error.message || "Failed to remove photo");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  return (
    <div className={`flex flex-col items-center gap-4 ${className}`}>
      {/* Photo Display */}
      <div
        className={`relative ${sizeClasses[size]} rounded-full overflow-hidden border-2 border-white/20 bg-white/10 backdrop-blur-sm group cursor-pointer transition-all duration-300 hover:border-[#B8460E]/50`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-1/2 h-1/2 text-white/60" />
          </div>
        )}

        {/* Upload Overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <Camera className="w-6 h-6 text-white" />
        </div>

        {/* Remove Button */}
        {previewUrl && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRemovePhoto();
            }}
            className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors duration-200"
            disabled={isUploading}
          >
            <X className="w-4 h-4 text-white" />
          </button>
        )}

        {/* Loading Overlay */}
        {isUploading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        )}
      </div>

      {/* Upload Instructions */}
      <div className="text-center">
        <p className="text-sm text-gray-300 mb-2">
          {previewUrl ? "Click to change photo" : "Click to upload photo"}
        </p>
        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Alternative Upload Button */}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        className="bg-white/10 hover:bg-white/20 text-white border-white/20"
      >
        <Upload className="w-4 h-4 mr-2" />
        {isUploading ? "Uploading..." : "Upload Photo"}
      </Button>
    </div>
  );
};

export default ProfilePhotoUpload;
